import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DownloadIcon,
  FileIcon,
  FileUpIcon,
  FolderIcon,
  FolderPlusIcon,
  MoveIcon,
  MessageSquarePlusIcon,
  PencilIcon,
  SearchIcon,
  Trash2Icon,
} from 'lucide-react';
import { type ChangeEvent, type JSX, useEffect, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  createOneDriveFolder,
  downloadOneDriveItem,
  getOneDriveItem,
  listOneDriveItems,
  moveOneDriveItem,
  oneDriveKeys,
  renameOneDriveItem,
  replaceOneDriveItemContent,
  searchOneDriveItems,
  trashOneDriveItem,
  uploadOneDriveFile,
  OneDriveApiError,
  type OneDriveAccount,
  type OneDriveItem,
  type OneDrivePage,
  type OneDriveScope,
} from '@/lib/api/onedrive.js';

import { ExternalFileViewer } from '@/features/artifacts/ExternalFileViewer.js';

import { oneDriveErrorPresentation } from './onedrive-error.js';
import {
  canMoveOneDriveItem,
  canMutateOneDriveItem,
} from './onedrive-permissions.js';

const STATUS_MESSAGES: Record<
  Exclude<OneDriveScope['status'], 'active'>,
  string
> = {
  folder_missing: 'The OneDrive folder no longer exists.',
  account_unavailable: 'The OneDrive account is unavailable.',
  scope_conflict: 'This folder overlaps another connected folder.',
  access_revoked: 'Access to this OneDrive folder was revoked.',
};

type Action =
  | { type: 'create' }
  | { type: 'rename'; item: OneDriveItem }
  | { type: 'move'; item: OneDriveItem }
  | { type: 'trash'; item: OneDriveItem };

export function OneDriveFileManager({
  workspaceId,
  account,
  scope,
  scopes,
  folderId,
  itemId = null,
  onNavigate,
  onAddToChat,
}: {
  workspaceId: string;
  account: OneDriveAccount;
  scope: OneDriveScope;
  scopes: readonly OneDriveScope[];
  folderId: string | null;
  itemId?: string | null;
  onNavigate: (folderId: string | null, itemId?: string | null) => void;
  onAddToChat?: (item: OneDriveItem) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const contextKey = `${workspaceId}:${scope.scopeId}:${folderId ?? ''}`;
  const [page, setPage] = useState({
    contextKey,
    cursor: null as string | null,
  });
  const cursor = page.contextKey === contextKey ? page.cursor : null;
  const [action, setAction] = useState<Action | null>(null);
  const [name, setName] = useState('');
  const [targetScopeId, setTargetScopeId] = useState(scope.scopeId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  /** The file open in the viewer — the same one a workspace artifact opens in. */
  const [viewing, setViewing] = useState<OneDriveItem | null>(null);

  function setCursor(nextCursor: string | null): void {
    setPage({ contextKey, cursor: nextCursor });
  }

  useEffect(() => {
    setSearch('');
    setSubmittedSearch('');
    setAction(null);
    setTargetScopeId(scope.scopeId);
    setError(null);
  }, [contextKey, scope.scopeId]);

  const listQuery = useQuery<OneDrivePage<OneDriveItem>>({
    queryKey: submittedSearch
      ? oneDriveKeys.search(workspaceId, scope.scopeId, submittedSearch, cursor)
      : oneDriveKeys.items(workspaceId, scope.scopeId, folderId, cursor),
    queryFn: () =>
      submittedSearch
        ? searchOneDriveItems(scope.scopeId, workspaceId, submittedSearch, {
            cursor,
          })
        : listOneDriveItems(scope.scopeId, workspaceId, {
            parentItemId: folderId,
            cursor,
          }),
    enabled: account.status === 'active' && scope.status === 'active',
    retry: false,
  });
  const selectedItemQuery = useQuery<OneDriveItem>({
    queryKey: oneDriveKeys.item(workspaceId, scope.scopeId, itemId ?? ''),
    queryFn: () => getOneDriveItem(scope.scopeId, itemId ?? '', workspaceId),
    enabled:
      account.status === 'active' &&
      scope.status === 'active' &&
      itemId !== null,
    retry: false,
  });

  useEffect(() => {
    if (
      cursor &&
      listQuery.error instanceof OneDriveApiError &&
      listQuery.error.code === 'ONEDRIVE_INVALID_CURSOR'
    ) {
      setPage({ contextKey, cursor: null });
    }
  }, [contextKey, cursor, listQuery.error]);

  if (scope.status !== 'active') {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
        <p className="font-medium text-destructive">
          {STATUS_MESSAGES[scope.status] ??
            'Access to this OneDrive folder was revoked.'}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Review the folder settings or reconnect the personal Microsoft
          account.
        </p>
      </div>
    );
  }

  if (account.status !== 'active') {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
        <p className="font-medium text-destructive">
          {account.status === 'reconsent_required'
            ? 'Reconnect the OneDrive account to continue.'
            : 'The OneDrive account is unavailable.'}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          File access stays closed until the personal Microsoft account is
          active again.
        </p>
      </div>
    );
  }

  const items = listQuery.data?.items ?? [];
  const canWrite = canMutateOneDriveItem(account, scope, false);
  /** A scope that is one file holds nothing else, so nothing is created or moved into it. */
  const canCreate = canWrite && !scope.isFile;
  const writableTargets = scopes.filter(
    (target) =>
      !target.isFile && canMoveOneDriveItem(account, scope, target, false),
  );
  /**
   * Whether an item is the scope's own root. Its content may be replaced when it is a file, but
   * renaming, moving or trashing it is refused on the server like any scope root.
   */
  const isScopeRoot = (item: OneDriveItem): boolean =>
    item.itemId === scope.rootItemId;

  function openAction(next: Action): void {
    setAction(next);
    setError(null);
    if (next.type === 'rename' || next.type === 'move') setName(next.item.name);
    else setName('');
    setTargetScopeId(scope.scopeId);
  }

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: oneDriveKeys.all });
  }

  async function submitAction(): Promise<void> {
    if (!action) return;
    setBusy(true);
    setError(null);
    try {
      if (action.type === 'create') {
        await createOneDriveFolder(scope.scopeId, {
          workspaceId,
          parentItemId: folderId,
          name: name.trim(),
        });
      } else if (action.type === 'rename') {
        await renameOneDriveItem(scope.scopeId, action.item.itemId, {
          workspaceId,
          name: name.trim(),
          ifMatch: action.item.etag ?? '',
        });
      } else if (action.type === 'move') {
        await moveOneDriveItem(scope.scopeId, action.item.itemId, {
          workspaceId,
          targetScopeId,
          name: name.trim() || undefined,
          ifMatch: action.item.etag ?? '',
        });
      } else {
        await trashOneDriveItem(
          scope.scopeId,
          action.item.itemId,
          workspaceId,
          action.item.etag ?? '',
        );
      }
      setAction(null);
      await refresh();
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  async function download(item: OneDriveItem): Promise<void> {
    setError(null);
    try {
      const blob = await downloadOneDriveItem(
        scope.scopeId,
        item.itemId,
        workspaceId,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (reason) {
      setError(reason);
    }
  }

  async function upload(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadOneDriveFile(scope.scopeId, workspaceId, folderId, file);
      await refresh();
      event.target.value = '';
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  async function replace(
    event: ChangeEvent<HTMLInputElement>,
    item: OneDriveItem,
  ): Promise<void> {
    const file = event.target.files?.[0];
    if (!file || !item.etag) return;
    setBusy(true);
    setError(null);
    try {
      await replaceOneDriveItemContent(
        scope.scopeId,
        item.itemId,
        workspaceId,
        item.etag,
        file,
      );
      await refresh();
      event.target.value = '';
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  const presentation = error ? oneDriveErrorPresentation(error) : null;
  const listPresentation = listQuery.isError
    ? oneDriveErrorPresentation(listQuery.error)
    : null;
  const actionItem = action && action.type !== 'create' ? action.item : null;
  const selectedPresentation = selectedItemQuery.isError
    ? oneDriveErrorPresentation(selectedItemQuery.error)
    : null;
  const selectedItem = selectedItemQuery.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-52 flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search this OneDrive folder"
            placeholder="Search names and contents"
            className="pl-9"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setCursor(null);
            setSubmittedSearch(search.trim());
          }}
        >
          Search
        </Button>
        {submittedSearch && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearch('');
              setSubmittedSearch('');
              setCursor(null);
            }}
          >
            Clear search
          </Button>
        )}
        {canCreate && (
          <>
            <Button
              variant="outline"
              onClick={() => openAction({ type: 'create' })}
            >
              <FolderPlusIcon /> Create folder
            </Button>
            <Button variant="outline" asChild>
              <label>
                <FileUpIcon /> {busy ? 'Uploading…' : 'Upload file'}
                <input
                  className="sr-only"
                  type="file"
                  onChange={(event) => void upload(event)}
                />
              </label>
            </Button>
          </>
        )}
      </div>

      {folderId && (
        <Button
          variant="ghost"
          className="self-start"
          onClick={() => onNavigate(null, null)}
        >
          Back to folder root
        </Button>
      )}

      {itemId && (
        <div className="rounded-xl border border-border p-4">
          <p className="font-medium">Selected item</p>
          {selectedItemQuery.isLoading ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Loading selected OneDrive item…
            </p>
          ) : selectedPresentation ? (
            <div className="mt-2 text-sm">
              <p className="text-destructive">{selectedPresentation.message}</p>
              <p className="text-muted-foreground">
                {selectedPresentation.remediation}
              </p>
            </div>
          ) : selectedItem ? (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm">{selectedItem.name}</p>
                <p className="text-xs text-muted-foreground">
                  {selectedItem.kind === 'folder'
                    ? 'Folder'
                    : `${selectedItem.size ?? 0} bytes`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedItem.kind === 'file' && onAddToChat && (
                  <Button
                    variant="outline"
                    onClick={() => onAddToChat(selectedItem)}
                  >
                    <MessageSquarePlusIcon /> Add to chat
                  </Button>
                )}
                {canWrite &&
                  selectedItem.kind === 'file' &&
                  selectedItem.etag && (
                    <Button variant="outline" asChild>
                      <label>
                        <FileUpIcon />
                        {busy ? 'Replacing…' : 'Replace content'}
                        <input
                          aria-label={`Replace content of ${selectedItem.name}`}
                          className="sr-only"
                          type="file"
                          disabled={busy}
                          onChange={(event) =>
                            void replace(event, selectedItem)
                          }
                        />
                      </label>
                    </Button>
                  )}
                <Button
                  variant="ghost"
                  onClick={() => onNavigate(folderId, null)}
                >
                  Clear selection
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {listQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading OneDrive files…</p>
      ) : listQuery.isError ? (
        <div className="rounded-lg border border-destructive/30 p-4 text-sm">
          <p className="font-medium text-destructive">
            {listPresentation?.message}
          </p>
          <p className="text-muted-foreground">
            {listPresentation?.remediation}
          </p>
          {listPresentation?.canRetry && (
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => void listQuery.refetch()}
            >
              Try again
            </Button>
          )}
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-dashed p-5 text-sm text-muted-foreground">
          {submittedSearch
            ? 'No matching files or folders.'
            : 'This folder is empty.'}
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          {items.map((item) => (
            <div
              key={item.itemId}
              className="flex flex-wrap items-center gap-2 border-b border-border/60 p-3 last:border-b-0"
            >
              <button
                type="button"
                aria-current={item.itemId === itemId ? 'true' : undefined}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                onClick={() => {
                  if (item.kind === 'folder') {
                    onNavigate(item.itemId, null);
                    return;
                  }
                  onNavigate(folderId, item.itemId);
                  setViewing(item);
                }}
              >
                {item.kind === 'folder' ? <FolderIcon /> : <FileIcon />}
                <span className="truncate">{item.name}</span>
              </button>
              {item.kind === 'file' && (
                <>
                  {onAddToChat && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Add ${item.name} to chat`}
                      onClick={() => onAddToChat(item)}
                    >
                      <MessageSquarePlusIcon />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Download ${item.name}`}
                    onClick={() => void download(item)}
                  >
                    <DownloadIcon />
                  </Button>
                </>
              )}
              {canWrite && item.etag && !isScopeRoot(item) && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Rename ${item.name}`}
                    onClick={() => openAction({ type: 'rename', item })}
                  >
                    <PencilIcon />
                  </Button>
                  {writableTargets.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Move ${item.name}`}
                      onClick={() => openAction({ type: 'move', item })}
                    >
                      <MoveIcon />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Move ${item.name} to recycle bin`}
                    onClick={() => openAction({ type: 'trash', item })}
                  >
                    <Trash2Icon />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {listQuery.data?.nextCursor && (
        <Button
          variant="outline"
          className="self-start"
          onClick={() => setCursor(listQuery.data?.nextCursor ?? null)}
        >
          Next page
        </Button>
      )}

      {submittedSearch && listQuery.data?.indexingDelayPossible && (
        <p className="text-xs text-muted-foreground">
          OneDrive search results can take time to reflect recent changes.
        </p>
      )}

      {presentation && !action && (
        <div className="rounded-lg border border-destructive/30 p-3 text-sm">
          <p className="font-medium text-destructive">{presentation.message}</p>
          <p className="text-muted-foreground">{presentation.remediation}</p>
        </div>
      )}

      {viewing && (
        <ExternalFileViewer
          workspaceId={workspaceId}
          source="OneDrive"
          file={{
            name: viewing.name,
            contentType: viewing.contentType ?? '',
            size: viewing.size,
            cacheKey:
              viewing.etag === null
                ? null
                : `od:${scope.scopeId}:${viewing.itemId}:${viewing.etag}`,
            loadBytes: () =>
              downloadOneDriveItem(scope.scopeId, viewing.itemId, workspaceId),
          }}
          webUrl={viewing.webUrl}
          canWrite={canWrite}
          location={{
            source: 'onedrive',
            scopeId: scope.scopeId,
            itemId: viewing.itemId,
          }}
          onAddToChat={onAddToChat ? () => onAddToChat(viewing) : undefined}
          onClose={() => setViewing(null)}
        />
      )}

      <AppDialog
        open={action !== null}
        onOpenChange={(open) => {
          if (!open) setAction(null);
        }}
        title={
          action?.type === 'create'
            ? 'Create OneDrive folder'
            : action?.type === 'rename'
              ? 'Rename OneDrive item'
              : action?.type === 'move'
                ? 'Move OneDrive item'
                : 'Move OneDrive item to recycle bin'
        }
        description={
          action?.type === 'trash'
            ? 'This moves the item to the OneDrive recycle bin. It does not permanently delete it.'
            : 'OneDrive checks the current eTag and never overwrites a conflict silently.'
        }
      >
        <div className="flex flex-col gap-4 pt-3">
          {action?.type !== 'trash' && (
            <label className="flex flex-col gap-1 text-sm">
              {action?.type === 'create' ? 'Folder name' : 'Item name'}
              <Input
                aria-label={
                  action?.type === 'create' ? 'Folder name' : 'Item name'
                }
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setError(null);
                }}
              />
            </label>
          )}
          {action?.type === 'move' && (
            <label className="flex flex-col gap-1 text-sm">
              Target connected folder
              <select
                className="h-10 rounded-md border border-input bg-background px-3"
                value={targetScopeId}
                onChange={(event) => {
                  setTargetScopeId(event.target.value);
                  setError(null);
                }}
              >
                {writableTargets.map((target) => (
                  <option key={target.scopeId} value={target.scopeId}>
                    {target.alias ?? target.originalName} —{' '}
                    {target.originalName}
                  </option>
                ))}
              </select>
            </label>
          )}
          {action?.type === 'trash' && actionItem && (
            <p className="text-sm text-muted-foreground">
              {actionItem.name} can be restored from the OneDrive recycle bin.
            </p>
          )}
          {presentation && (
            <div className="rounded-lg border border-destructive/30 p-3 text-sm">
              <p className="font-medium text-destructive">
                {presentation.message}
              </p>
              <p className="text-muted-foreground">
                {presentation.remediation}
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAction(null)}>
              Cancel
            </Button>
            <Button
              variant={action?.type === 'trash' ? 'destructive' : 'default'}
              disabled={
                busy ||
                (presentation !== null && !presentation.canRetry) ||
                (action?.type !== 'trash' && name.trim().length === 0) ||
                (action?.type === 'move' && targetScopeId.length === 0)
              }
              onClick={() => void submitAction()}
            >
              {action?.type === 'create'
                ? 'Create folder'
                : action?.type === 'rename'
                  ? 'Rename item'
                  : action?.type === 'move'
                    ? 'Move item'
                    : 'Move to recycle bin'}
            </Button>
          </div>
        </div>
      </AppDialog>
    </div>
  );
}
